import React, { useEffect, useMemo, useState } from 'react';
import { X, FileText, Calculator, Gavel, GitBranch, IndianRupee, Plus, Send, Sparkles, Trash2, Check, Zap, Lock } from 'lucide-react';

const SECTION_OPTIONS = ['Pre-Production', 'Concept Art', 'Animatics', 'Asset Build', 'Rigging', 'Animation', 'Lighting & Rendering', 'FX & Compositing', 'Sound Design', 'LRC / Final QC'];

// Department taxonomy for the Deliverables picker
const DEPARTMENTS = [
  { key: 'concept_art', label: 'Concept Art', kind: 'asset' },
  { key: 'animatics', label: 'Animatics', kind: 'shot' },
  { key: 'asset_build', label: 'Asset Build', kind: 'asset' },
  { key: 'rigging', label: 'Rigging', kind: 'asset' },
  { key: 'animation', label: 'Animation', kind: 'shot' },
  { key: 'lighting', label: 'Lighting', kind: 'shot' },
  { key: 'fx', label: 'FX', kind: 'shot' },
  { key: 'compositing', label: 'Compositing', kind: 'shot' },
  { key: 'sound', label: 'Sound', kind: 'sound' },
];

const STAGE_OPTIONS = ['concept_art', 'animatics', 'asset_build', 'rigging', 'layout', 'animation', 'lighting', 'fx', 'comp', 'render', 'lrc', 'sound_design', 'sound'];

const APPROVAL_AUTHORITIES = ['Founder / CEO', 'Production Manager', 'Supervisor', 'Executive Producer', 'Line Producer'];

// Work Unit Table: Pipeline → default Task name + billing Unit
const PIPELINE_DEFAULTS = {
  concept_art:  { task: 'Concept Art Package',          unit: 'asset' },
  animatics:    { task: 'Storyboarding & Animatics',    unit: 'minute' },
  asset_build:  { task: 'Character Modeling',            unit: 'asset' },
  rigging:      { task: 'Character Rig',                 unit: 'rig' },
  layout:       { task: 'Layout',                        unit: 'second' },
  animation:    { task: 'Animation',                     unit: 'second' },
  lighting:     { task: 'Lighting',                      unit: 'shot' },
  fx:           { task: 'FX',                            unit: 'shot' },
  comp:         { task: 'Compositing',                   unit: 'shot' },
  render:       { task: 'Rendering',                     unit: 'shot' },
  lrc:          { task: 'LRC / Final QC',                unit: 'shot' },
  sound_design: { task: 'Sound Design & Mix',            unit: 'minute' },
  sound:        { task: 'Sound Design & Mix',            unit: 'minute' },
};

// Section → default Pipeline mapping
const SECTION_TO_STAGE = {
  'Pre-Production':       'concept_art',
  'Concept Art':          'concept_art',
  'Animatics':            'animatics',
  'Asset Build':          'asset_build',
  'Rigging':              'rigging',
  'Animation':            'animation',
  'Lighting & Rendering': 'lighting',
  'FX & Compositing':     'fx',
  'Sound Design':         'sound_design',
  'LRC / Final QC':       'lrc',
};

const emptyLine = { section: 'Asset Build', stage: 'asset_build', task: 'Character Modeling', unit: 'asset', quantity: 0, rate: 0, notes: '' };

const currency = value => `\u20B9${Number(value || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

function suggestedLines(blueprint) {
  const s = blueprint?.suggested_scope || {};
  const c = blueprint?.counts || {};
  return [
    { section: 'Concept Art', stage: 'concept_art', task: 'Concept Art Package', unit: 'asset', quantity: s.concept_art || 0, rate: 0, notes: 'Character / environment / prop exploration' },
    { section: 'Asset Build', stage: 'asset_build', task: 'Character Modeling', unit: 'asset', quantity: c.characters || 0, rate: 0, notes: 'Model + texture-ready asset' },
    { section: 'Asset Build', stage: 'asset_build', task: 'Prop / Environment Modeling', unit: 'asset', quantity: (c.props || 0) + (c.environments || 0), rate: 0, notes: 'Props and environment assets' },
    { section: 'Rigging', stage: 'rigging', task: 'Character Rig', unit: 'rig', quantity: s.rigging || 0, rate: 0, notes: 'Animation-ready rig' },
    { section: 'Animation', stage: 'layout', task: 'Layout', unit: 'second', quantity: s.layout || 0, rate: 0, notes: 'Camera layout & blocking' },
    { section: 'Animation', stage: 'animation', task: 'Animation', unit: 'second', quantity: c.animation_seconds || 0, rate: 0, notes: `${s.animation || 0} shots` },
    { section: 'Lighting & Rendering', stage: 'lighting', task: 'Lighting', unit: 'shot', quantity: s.lighting || 0, rate: 0, notes: '' },
    { section: 'Lighting & Rendering', stage: 'render', task: 'Rendering', unit: 'shot', quantity: s.render || 0, rate: 0, notes: '' },
    { section: 'FX & Compositing', stage: 'fx', task: 'FX', unit: 'shot', quantity: s.fx || 0, rate: 0, notes: '' },
    { section: 'FX & Compositing', stage: 'comp', task: 'Compositing', unit: 'shot', quantity: s.comp || 0, rate: 0, notes: '' },
    { section: 'Sound Design', stage: 'sound_design', task: 'Sound Design & Mix', unit: 'minute', quantity: c.animation_minutes || 0, rate: 0, notes: 'SFX, Foley, ambient, dialogue sync' },
    { section: 'LRC / Final QC', stage: 'lrc', task: 'LRC / Final QC', unit: 'shot', quantity: s.lrc || 0, rate: 0, notes: '' },
  ].filter(line => Number(line.quantity || 0) > 0 || ['animation', 'asset_build'].includes(line.stage));
}

export default function WorkOrderBuilder({ api, user, selectedVendor, selectedKind, projects, onClose, onCreated, editingWO }) {
  const [projectId, setProjectId] = useState(editingWO?.project_id || projects?.[0]?.id || '');
  const [blueprint, setBlueprint] = useState(null);
  const [saving, setSaving] = useState(false);
  // Auto-populate: pulls allotted-but-unbilled tasks for the chosen
  // vendor/freelancer through /work-orders/auto-populate and replaces
  // the rate-card lines with computed qty × project rate values.
  const [autoBusy, setAutoBusy] = useState(false);
  const [autoSummary, setAutoSummary] = useState(null); // {tasks_to_bill_count, already_billed_task_count, rate_card_locked, rate_card_missing}

  // ─── DELIVERABLES PICKER STATE (replaces old Scope + Deliverables summary) ───
  const editDept = editingWO?.scope?.department || 'asset_build';
  const editSelected = new Set((editingWO?.scope?.selected_deliverables || []).map(d => d.id).filter(Boolean));
  const editNotesMap = Object.fromEntries((editingWO?.scope?.selected_deliverables || []).map(d => [d.id, d.notes || '']));
  const [department, setDepartment] = useState(editDept);
  const [deliverablesPool, setDeliverablesPool] = useState({ characters: [], props: [], environments: [], shots: [] });
  const [selectedDeliverables, setSelectedDeliverables] = useState(editSelected);
  const [deliverableNotes, setDeliverableNotes] = useState(editNotesMap);
  const [deliverablesLoading, setDeliverablesLoading] = useState(false);

  // Pre-fill from existing WO if editing
  const editLines = editingWO?.lines || editingWO?.rate_card || [];
  const editInfo = editingWO?.project_info || {};
  const editLegal = editingWO?.legal_terms || {};
  const editPayment = editingWO?.payment_terms?.milestones || [];
  const editRevision = editingWO?.revision_policy || {};
  const editDeliverables = editingWO?.deliverables || {};

  const [lines, setLines] = useState(
    editLines.length > 0
      ? editLines.map(l => ({ section: l.section || '', stage: l.stage || l.raw_stage || '', task: l.task || l.label || '', unit: l.unit || l.billing_unit || '', quantity: l.quantity || 0, rate: l.rate || 0, notes: l.notes || '' }))
      : [emptyLine]
  );
  const [form, setForm] = useState({
    scope_title: editingWO?.scope_title || '',
    project_type: editInfo.project_type || 'Series',
    start_date: editInfo.start_date || '',
    end_date: editInfo.end_date || '',
    approval_authority: editInfo.approval_authority || 'Founder / CEO',
    resolution: editDeliverables.resolution || '1920x1080',
    format: editDeliverables.format || 'MOV / MP4 / EXR as applicable',
    frame_rate: editDeliverables.frame_rate || '24',
    audio: editDeliverables.audio || 'Yes',
    sound_mix: editDeliverables.sound_mix || 'Stereo',
    sound_format: editDeliverables.sound_format || 'WAV',
    sound_sample_rate: editDeliverables.sound_sample_rate || '48 kHz',
    sound_bit_depth: editDeliverables.sound_bit_depth || '24-bit',
    exclusions: (editingWO?.scope_exclusions || []).join('\n') || 'Voiceover, music, source files, and extra revision rounds unless explicitly listed.',
    gst_percent: editingWO?.tax?.gst_percent ?? 18,
    advance_percent: editPayment.find(m => m.name === 'Advance')?.percent ?? 30,
    mid_percent: editPayment.find(m => m.name === 'Mid Milestone')?.percent ?? 40,
    final_percent: editPayment.find(m => m.name === 'Final Delivery')?.percent ?? 30,
    revision_rounds: editRevision.included_rounds ?? 2,
    minor_revision_cost: editRevision.minor_extra_cost ?? 0,
    major_revision_cost: editRevision.major_extra_cost ?? 0,
    ip_ownership: editLegal.ip_ownership || 'Client after full payment clearance',
    usage_rights: editLegal.usage_rights || 'Project-specific usage only',
    confidentiality: editLegal.confidentiality || 'Strict NDA and platform-only file exchange',
    termination: editLegal.termination || 'Either party may terminate on written notice for material breach.',
    delay_penalty: editLegal.delay_penalty || 'As mutually agreed in writing.',
    notes: editingWO?.notes || '',
  });

  useEffect(() => {
    if (!projectId && projects?.[0]?.id) setProjectId(projects[0].id);
  }, [projects, projectId]);

  useEffect(() => {
    if (!projectId) return;
    api.get(`/work-orders/blueprint/${projectId}`).then(r => {
      setBlueprint(r.data);
      // Only auto-fill from blueprint if NOT editing an existing WO
      if (!editingWO) {
        setLines(suggestedLines(r.data));
        setForm(f => ({
          ...f,
          scope_title: `${r.data.project.name} Production Work Order`,
          project_type: r.data.project.project_type || f.project_type,
          end_date: r.data.project.deadline || f.end_date,
          resolution: r.data.project.resolution || f.resolution,
          frame_rate: String(r.data.project.fps || f.frame_rate),
        }));
      }
    }).catch(() => setBlueprint(null));
  }, [api, projectId]);

  // Fetch deliverables when project / department / vendor changes.
  // Sound dept uses hard-coded deliverables — skip the fetch.
  //
  // Founder spec (May 19 2026): scope the deliverables list to ONLY the
  // shots/assets allotted to the selected vendor/freelancer. We pass
  // `assignee_user_id` or `vendor_id` so the backend filters tasks by
  // `assigned_to`. Without this, all 173 shots showed up regardless of
  // allotment — the founder had to manually pick which 20 went to Joseph
  // even though the system already knows.
  //
  // EDIT mode (May 20 2026): also pass `wo_id` so the backend keeps THIS
  // WO's own shots visible (otherwise they'd be filtered out as "already
  // billed elsewhere") AND continues to exclude shots on OTHER active WOs
  // for the same assignee (prevents cross-WO duplicate billing).
  useEffect(() => {
    if (!projectId || department === 'sound') return;
    setDeliverablesLoading(true);
    let url = `/work-orders/deliverables/${projectId}?department=${department}`;
    if (selectedVendor) {
      const assigneeParam = selectedKind === 'freelancer'
        ? `assignee_user_id=${selectedVendor}`
        : `vendor_id=${selectedVendor}`;
      url += `&${assigneeParam}`;
    }
    if (editingWO?.id) {
      url += `&wo_id=${editingWO.id}`;
    }
    api.get(url)
      .then(r => {
        const pool = r.data || { characters: [], props: [], environments: [], shots: [] };
        setDeliverablesPool(pool);
        // When the backend has filtered the list to just this assignee's
        // allotments AND we are NOT editing, pre-check every row so the
        // founder doesn't have to click "Select All". In edit mode the
        // pre-selection comes from `editSelected` (only the items that
        // were on this WO originally) — never auto-add new shots in edit.
        if (pool.assignee_filtered && !editingWO) {
          const allIds = new Set([
            ...(pool.characters || []).map(c => c.id || c.code),
            ...(pool.props || []).map(p => p.id || p.code),
            ...(pool.environments || []).map(e => e.id || e.code),
            ...(pool.shots || []).map(s => s.id || s.parent_code),
          ].filter(Boolean));
          setSelectedDeliverables(allIds);
        }
      })
      .catch(() => setDeliverablesPool({ characters: [], props: [], environments: [], shots: [] }))
      .finally(() => setDeliverablesLoading(false));
  }, [api, projectId, department, selectedVendor, selectedKind, editingWO]);

  const deptObj = DEPARTMENTS.find(d => d.key === department) || DEPARTMENTS[2];
  const isSound = department === 'sound';
  const isAssetDept = ['concept_art', 'asset_build', 'rigging'].includes(department);
  const isShotDept = ['animation', 'lighting', 'fx', 'compositing', 'animatics'].includes(department);

  const availableItems = useMemo(() => {
    if (isSound) {
      return [
        { id: 'sound_bgm', code: 'BGM', name: 'BGM', category: 'Sound', kind: 'bgm' },
        { id: 'sound_foley', code: 'Foley', name: 'Foley', category: 'Sound', kind: 'foley' },
        { id: 'sound_vo', code: 'Voice Over', name: 'Voice Over', category: 'Sound', kind: 'vo' },
      ];
    }
    if (isAssetDept) {
      return [
        ...(deliverablesPool.characters || []).map(c => ({ ...c, category: 'Character' })),
        ...(deliverablesPool.props || []).map(p => ({ ...p, category: 'Prop' })),
        ...(deliverablesPool.environments || []).map(e => ({ ...e, category: 'Environment' })),
      ];
    }
    if (isShotDept) {
      return (deliverablesPool.shots || []).map(s => ({ ...s, category: 'Shot', code: s.parent_code || s.name }));
    }
    return [];
  }, [deliverablesPool, isAssetDept, isShotDept, isSound]);

  const toggleDeliverable = (id) => {
    setSelectedDeliverables(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedDeliverables.size === availableItems.length) {
      setSelectedDeliverables(new Set());
    } else {
      setSelectedDeliverables(new Set(availableItems.map(i => i.id || i.code)));
    }
  };

  const totals = useMemo(() => {
    const subtotal = lines.reduce((sum, l) => sum + Number(l.quantity || 0) * Number(l.rate || 0), 0);
    const gst = subtotal * Number(form.gst_percent || 0) / 100;
    const total = subtotal + gst;
    return { subtotal, gst, total, advance: total * Number(form.advance_percent || 0) / 100, mid: total * Number(form.mid_percent || 0) / 100, final: total * Number(form.final_percent || 0) / 100 };
  }, [lines, form.gst_percent, form.advance_percent, form.mid_percent, form.final_percent]);

  const updateLine = (index, patch) => {
    setLines(prev => prev.map((line, i) => {
      if (i !== index) return line;
      const updated = { ...line, ...patch };
      // Auto-fill Task + Unit when Pipeline (stage) changes
      if (patch.stage) {
        const defaults = PIPELINE_DEFAULTS[patch.stage];
        if (defaults) {
          updated.task = defaults.task;
          updated.unit = defaults.unit;
        }
      }
      // Auto-fill Pipeline + Task + Unit when Section changes
      if (patch.section && !patch.stage) {
        const defaultStage = SECTION_TO_STAGE[patch.section];
        if (defaultStage) {
          updated.stage = defaultStage;
          const defaults = PIPELINE_DEFAULTS[defaultStage];
          if (defaults) {
            updated.task = defaults.task;
            updated.unit = defaults.unit;
          }
        }
      }
      return updated;
    }));
  };
  const field = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  // ── AUTO-POPULATE from allotments ──
  // Founder spec (May 14, 2026): "When I allot shots/assets to a vendor,
  // the WO should auto-populate what items have been assigned." Calls
  // /work-orders/auto-populate which groups the vendor's unbilled tasks
  // by department, multiplies qty × project-rate-card rate, and returns
  // ready-to-insert line items.
  //
  // Founder follow-up (May 19, 2026): "as soon as shots are assigned >>
  // work order should be autofilled". The fn now accepts `silent=true` so
  // it can fire automatically on WO open (no confirm prompt, quieter toast).
  const autoPopulate = async (silent = false) => {
    if (!projectId || !selectedVendor) {
      if (!silent) {
        try { (await import('sonner')).toast.error('Pick a project and vendor/freelancer first'); }
        catch (_e) { alert('Pick a project and vendor/freelancer first'); }
      }
      return;
    }
    // Only show the destructive confirm when the user invoked us manually
    // AND the current sheet already has real qty entered. On auto-trigger
    // (silent mode) the sheet is the default suggestedLines() scaffolding,
    // which is safe to overwrite with real allotment data.
    if (!silent && (lines.length > 1 || (lines[0] && Number(lines[0].quantity) > 0))) {
      const ok = window.confirm(
        'This will REPLACE the current rate-card lines with the auto-populated allotments.\n\nContinue?'
      );
      if (!ok) return;
    }
    setAutoBusy(true);
    try {
      const params = selectedKind === 'freelancer'
        ? `assignee_user_id=${selectedVendor}`
        : `vendor_id=${selectedVendor}`;
      // EDIT mode: pass wo_id so this WO's tasks stay billable (otherwise
      // they'd be silently excluded as "already billed" and the auto-populate
      // would empty out the rate card on every edit).
      const woSuffix = editingWO?.id ? `&wo_id=${editingWO.id}` : '';
      const r = await api.get(`/work-orders/auto-populate?project_id=${projectId}&${params}${woSuffix}`);
      const data = r.data || {};
      setAutoSummary({
        tasks_to_bill_count: data.tasks_to_bill_count || 0,
        already_billed_task_count: data.already_billed_task_count || 0,
        rate_card_locked: !!data.rate_card_locked,
        rate_card_missing: !!data.rate_card_missing,
        subtotal: data.subtotal || 0,
        assignee_name: data.assignee?.name || '',
      });
      const incoming = Array.isArray(data.lines) ? data.lines : [];
      if (incoming.length === 0) {
        // In silent mode we DON'T toast "no allotments" — the empty result
        // is informational enough (the rate-card sheet stays as the default
        // suggested-lines scaffold from the blueprint).
        if (!silent) {
          try {
            (await import('sonner')).toast.info(
              data.already_billed_task_count > 0
                ? `All allotments for ${data.assignee?.name} are already on a Work Order.`
                : `No allotments found for ${data.assignee?.name || 'this assignee'} on this project.`
            );
          } catch (_e) { /* no-op */ }
        }
        setAutoBusy(false);
        return;
      }
      setLines(incoming.map(l => ({
        section: l.section || '',
        stage: l.stage || '',
        task: l.task || '',
        unit: l.unit || '',
        quantity: Number(l.quantity || 0),
        rate: Number(l.rate || 0),
        notes: l.notes || '',
        // Preserve linked task ids so the WO knows which tasks it covers.
        task_ids: l.task_ids || [],
      })));
      try {
        (await import('sonner')).toast.success(
          `Auto-populated ${incoming.length} line${incoming.length === 1 ? '' : 's'} from ${data.tasks_to_bill_count} task${data.tasks_to_bill_count === 1 ? '' : 's'}`
        );
      } catch (_e) { /* no-op */ }
    } catch (e) {
      // Only toast errors on explicit user action — a silent auto-trigger
      // failure should NOT spam the user; the manual button is still there.
      if (!silent) {
        const msg = e?.response?.data?.detail || 'Auto-populate failed';
        try { (await import('sonner')).toast.error(msg); } catch (_e) { alert(msg); }
      } else {
        // eslint-disable-next-line no-console
        console.warn('Silent auto-populate failed:', e?.response?.data?.detail || e?.message);
      }
    } finally {
      setAutoBusy(false);
    }
  };

  // ── ONE-SHOT AUTO-TRIGGER (founder ask May 19 2026) ──
  // The instant the WO modal opens with a pre-selected vendor/freelancer
  // AND a project, pull their unbilled allotments without waiting for the
  // user to find the violet button.
  //
  // IMPORTANT: We depend on `blueprint` being loaded too. The blueprint
  // useEffect runs `setLines(suggestedLines(...))` after fetching from
  // `/work-orders/blueprint/{projectId}`. If we fire autoPopulate before
  // that settles, our lines get overwritten by blueprint a moment later
  // (caused the "stale lines after project switch" bug — May 19 2026).
  // Waiting until blueprint is in state guarantees blueprint's setLines
  // already happened, so our autoPopulate setLines wins.
  //
  // We don't run for edit mode (existing WO is already populated) and
  // only once per `(projectId, vendor, kind)` combo.
  const [autoFiredFor, setAutoFiredFor] = useState('');
  useEffect(() => {
    if (editingWO) return;
    if (!projectId || !selectedVendor || !blueprint) return;
    // Confirm blueprint matches current projectId — otherwise we'd auto-fire
    // for the old project's blueprint before the new one loaded.
    if (blueprint?.project?.id && blueprint.project.id !== projectId) return;
    const key = `${projectId}::${selectedVendor}::${selectedKind || ''}`;
    if (autoFiredFor === key) return;
    setAutoFiredFor(key);
    // Tiny defer so React commits the blueprint setLines first, then we
    // overlay the auto-populate result on top.
    const t = setTimeout(() => { autoPopulate(true); }, 80);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, selectedVendor, selectedKind, editingWO, blueprint]);

  const create = async () => {
    // Re-issue confirmation — when editing a WO that's already been accepted
    // by the vendor, warn the user that saving will reset the WO to ISSUED
    // and the vendor will need to re-accept the new commercial terms.
    let acknowledgeReaccept = false;
    if (editingWO && editingWO.status === 'accepted') {
      const ok = window.confirm(
        'This WO is already accepted by the vendor.\n\n'
        + 'Editing it will reset its status to ISSUED and require the vendor to '
        + 're-accept the new commercial terms. They will receive a fresh email '
        + 'notification.\n\nProceed with re-issue?',
      );
      if (!ok) return;
      acknowledgeReaccept = true;
    }
    setSaving(true);
    try {
      const project = blueprint?.project || projects.find(p => p.id === projectId) || {};
      const selectedItems = availableItems.filter(i => selectedDeliverables.has(i.id || i.code));
      const deliverablesPayload = isSound
        ? { sound_mix: form.sound_mix, sound_format: form.sound_format, sound_sample_rate: form.sound_sample_rate, sound_bit_depth: form.sound_bit_depth }
        : { resolution: form.resolution, format: form.format, frame_rate: form.frame_rate, audio: form.audio };
      const payload = {
        // Founder rule: freelancers don't have a db.vendors row, so send
        // their selected id under assignee_user_id instead of vendor_id.
        ...(selectedKind === 'freelancer'
          ? { assignee_user_id: selectedVendor }
          : { vendor_id: selectedVendor }),
        project_id: projectId,
        scope_title: form.scope_title,
        project_info: { client_name: project.client_name || '', project_type: form.project_type, start_date: form.start_date, end_date: form.end_date, prepared_by: user?.full_name || user?.email || '', approval_authority: form.approval_authority },
        scope: {
          ...(blueprint?.suggested_scope || {}),
          animation_minutes: blueprint?.counts?.animation_minutes || 0,
          department,
          selected_deliverables: selectedItems.map(i => ({
            id: i.id,
            code: i.code,
            name: i.name,
            kind: i.kind || i.category?.toLowerCase(),
            notes: deliverableNotes[i.id || i.code] || '',
          })),
        },
        deliverables: deliverablesPayload,
        scope_exclusions: form.exclusions.split('\n').map(x => x.trim()).filter(Boolean),
        rate_card: lines,
        // Collect every task_id surfaced by auto-populate so this WO
        // "consumes" those allotments and they get dedup'd from the
        // next vendor auto-populate run.
        task_ids: Array.from(new Set(lines.flatMap(l => l.task_ids || []))),
        tax: { gst_percent: Number(form.gst_percent || 0) },
        payment_terms: { milestones: [
          { name: 'Advance', trigger: 'On Work Order acceptance', percent: Number(form.advance_percent || 0) },
          { name: 'Mid Milestone', trigger: 'On asset / animation milestone completion', percent: Number(form.mid_percent || 0) },
          { name: 'Final Delivery', trigger: 'On final approved delivery', percent: Number(form.final_percent || 0) },
        ] },
        revision_policy: { included_rounds: Number(form.revision_rounds || 0), minor_extra_cost: Number(form.minor_revision_cost || 0), major_extra_cost: Number(form.major_revision_cost || 0) },
        dependencies: { work_starts_after: ['Approved Concept Art', 'Approved Script / Animatics'], stage_rule: 'Each stage depends on previous stage approval' },
        approval_workflow: { approvals_via_platform: true, approval_triggers_next_department: true, final_delivery_after_payment_clearance: true },
        legal_terms: { ip_ownership: form.ip_ownership, usage_rights: form.usage_rights, confidentiality: form.confidentiality, termination: form.termination, delay_penalty: form.delay_penalty },
        signoff: { client_signature: '', company_representative: form.approval_authority, date: '' },
        notes: form.notes,
        // Re-issue acknowledgement — backend requires this flag to allow PUT
        // on accepted WOs. Cleared status → 'issued'; vendor must re-accept.
        ...(acknowledgeReaccept ? { acknowledge_reaccept: true } : {}),
      };
      const res = editingWO
        ? await api.put(`/work-orders/${editingWO.id}`, payload)
        : await api.post('/work-orders/generate', payload);
      // On re-issue, surface a success toast so the user knows the vendor
      // will receive a fresh email and the WO is back in ISSUED state.
      if (acknowledgeReaccept) {
        try {
          (await import('sonner')).toast.success(
            `${res.data?.work_order_no || 'WO'} re-issued · vendor will re-accept`,
          );
        } catch (_e) { /* no-op if sonner not loaded */ }
      }
      onCreated(res.data);
    } catch (e) {
      console.error('Work order save failed:', e);
      // ── DUPLICATE VIGIL (May 20 2026) ──
      // Backend returns 409 with {error: 'duplicate_work_order', message,
      // conflicts: [{work_order_no, work_order_id, assignee_name,
      // overlapping_task_ids[], overlapping_deliverable_codes[]}]}. We
      // build a clear confirm() so the founder knows exactly which WOs
      // already cover these tasks. If they choose to override, retry
      // with force_duplicate=true (admin escape hatch).
      const data = e?.response?.data;
      const detail = data?.detail;
      const isDup = e?.response?.status === 409
        && (typeof detail === 'object')
        && detail?.error === 'duplicate_work_order';
      if (isDup) {
        const conflicts = detail.conflicts || [];
        const lines = conflicts.slice(0, 5).map(c => {
          const hits = (c.overlapping_task_ids?.length || 0) + (c.overlapping_deliverable_codes?.length || 0);
          // Show the conflicting department(s) so the founder
          // immediately knows whether this is a true clash (same dept)
          // or a stale flag. Founder fix Feb 9, 2026.
          const dept = Array.isArray(c.overlapping_departments) && c.overlapping_departments.length
            ? ` · ${c.overlapping_departments.join(', ').toUpperCase()}`
            : '';
          return `  • ${c.work_order_no} — ${c.assignee_name || 'unknown'} (${hits} overlapping${dept})`;
        });
        const more = conflicts.length > 5 ? `\n  + ${conflicts.length - 5} more` : '';
        const proceed = window.confirm(
          `🛑 DUPLICATE WORK ORDER DETECTED\n\n` +
          `These tasks/deliverables are already on ${conflicts.length} active Work Order${conflicts.length !== 1 ? 's' : ''}:\n` +
          `${lines.join('\n')}${more}\n\n` +
          `Recommended: cancel here and EDIT the existing WO instead of creating a duplicate.\n\n` +
          `Click OK to FORCE-CREATE this WO anyway (will result in double-billing if not handled carefully).\n` +
          `Click Cancel to go back and review.`
        );
        if (proceed) {
          // Retry with force flag — same payload, override the duplicate guard.
          try {
            const res2 = await api.post('/work-orders/generate', { ...payload, force_duplicate: true });
            try {
              (await import('sonner')).toast.warning(
                `${res2.data?.work_order_no || 'WO'} created with DUPLICATE OVERRIDE — verify billing`,
              );
            } catch (_e2) { /* no-op */ }
            onCreated(res2.data);
          } catch (e2) {
            const m2 = e2?.response?.data?.detail || e2?.message || 'Override failed';
            try { (await import('sonner')).toast.error(typeof m2 === 'string' ? m2 : 'Override failed'); } catch (_e3) { alert(typeof m2 === 'string' ? m2 : 'Override failed'); }
          }
        }
        setSaving(false);
        return;
      }
      const msg = (typeof detail === 'string' ? detail : detail?.message) || e?.message || 'Failed to save work order';
      try { (await import('sonner')).toast.error(msg); } catch (_e) { alert(msg); }
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm" data-testid="work-order-builder-modal">
      <div className="h-full flex flex-col bg-zinc-950 text-zinc-100">
        <div className="px-6 py-4 border-b border-zinc-800 flex items-center justify-between bg-zinc-950/95">
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-amber-400 flex items-center gap-2"><Sparkles className="w-3.5 h-3.5" /> Production Work Order Builder</p>
            <h2 className="text-2xl font-heading font-bold text-white mt-1" data-testid="wo-builder-title">
              {editingWO
                ? (editingWO.status === 'accepted' ? 'Re-issue Work Order' : 'Edit Work Order')
                : 'Financial + Production Blueprint'}
            </h2>
            {editingWO && editingWO.status === 'accepted' && (
              <p className="text-[11px] text-amber-400 mt-1 flex items-center gap-1.5" data-testid="wo-reissue-warning">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse"></span>
                Already accepted by vendor — saving will reset to ISSUED and require re-acceptance
              </p>
            )}
          </div>
          <button onClick={onClose} data-testid="work-order-builder-close" className="w-9 h-9 rounded-sm border border-zinc-800 hover:bg-zinc-900 flex items-center justify-center"><X className="w-4 h-4" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-1 xl:grid-cols-[1fr_360px] gap-5">
            <div className="space-y-5">

              {/* ─── 1. PROJECT INFORMATION ─── */}
              <section className="bg-zinc-900 border border-zinc-800 rounded-sm p-4" data-testid="work-order-project-section">
                <h3 className="text-xs uppercase tracking-wider text-zinc-300 mb-3 flex items-center gap-2"><FileText className="w-4 h-4 text-amber-400" /> 1. Project Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1 block">Project</label>
                    <select value={projectId} onChange={e => setProjectId(e.target.value)} data-testid="work-order-project-select" className="w-full h-10 bg-zinc-950 border border-zinc-800 px-3 text-xs focus:outline-none rounded-sm">
                      {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1 block">Scope Title</label>
                    <input value={form.scope_title} onChange={e => field('scope_title', e.target.value)} data-testid="work-order-scope-title-input" className="w-full h-10 bg-zinc-950 border border-zinc-800 px-3 text-xs focus:outline-none rounded-sm" placeholder="Scope title" />
                  </div>
                  <div>
                    <label className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1 block">Project Type</label>
                    <input value={form.project_type} onChange={e => field('project_type', e.target.value)} data-testid="work-order-project-type-input" className="w-full h-10 bg-zinc-950 border border-zinc-800 px-3 text-xs focus:outline-none rounded-sm" placeholder="Film / Series / OTT" />
                  </div>
                  <div>
                    <label className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1 block">Start Date</label>
                    <input type="date" value={form.start_date} onChange={e => field('start_date', e.target.value)} data-testid="work-order-start-date-input" className="w-full h-10 bg-zinc-950 border border-zinc-800 px-3 text-xs focus:outline-none rounded-sm" />
                  </div>
                  <div>
                    <label className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1 block">End Date</label>
                    <input type="date" value={form.end_date} onChange={e => field('end_date', e.target.value)} data-testid="work-order-end-date-input" className="w-full h-10 bg-zinc-950 border border-zinc-800 px-3 text-xs focus:outline-none rounded-sm" />
                  </div>
                  <div>
                    <label className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1 block">Approval Authority</label>
                    <select value={form.approval_authority} onChange={e => field('approval_authority', e.target.value)} data-testid="work-order-approval-authority-select" className="w-full h-10 bg-zinc-950 border border-zinc-800 px-3 text-xs focus:outline-none rounded-sm">
                      {APPROVAL_AUTHORITIES.map(a => <option key={a} value={a}>{a}</option>)}
                    </select>
                  </div>
                </div>
              </section>

              {/* ─── 2. DEPARTMENT + DELIVERABLES ─── */}
              <section className="bg-zinc-900 border border-zinc-800 rounded-sm p-4" data-testid="work-order-deliverables-section">
                <h3 className="text-xs uppercase tracking-wider text-zinc-300 mb-3 flex items-center gap-2"><GitBranch className="w-4 h-4 text-cyan-400" /> 2. Department + Deliverables</h3>
                <div className="mb-3">
                  <label className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1 block">Department</label>
                  <select value={department} onChange={e => { setDepartment(e.target.value); setSelectedDeliverables(new Set()); setDeliverableNotes({}); }} data-testid="work-order-department-select"
                    className="w-full md:w-1/2 h-10 bg-zinc-950 border border-zinc-800 px-3 text-xs rounded-sm focus:outline-none focus:border-cyan-500">
                    {DEPARTMENTS.map(d => <option key={d.key} value={d.key}>{d.label}</option>)}
                  </select>
                </div>

                <div className="bg-zinc-950 border border-zinc-800 rounded-sm p-4" data-testid="work-order-deliverables-picker">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-[11px] uppercase tracking-wider text-zinc-400 flex items-center gap-2">
                      <FileText className="w-3.5 h-3.5 text-cyan-400" /> Select Deliverables
                      {deliverablesPool.assignee_filtered && (
                        <span className="ml-1 px-1.5 py-0.5 rounded-sm bg-violet-500/15 text-violet-300 text-[9px] uppercase tracking-wider" data-testid="work-order-deliverables-filtered-badge">
                          From Allotment
                        </span>
                      )}
                    </h4>
                    <div className="flex items-center gap-3">
                      <span className="text-[10px] text-zinc-500" data-testid="work-order-deliverables-count">{selectedDeliverables.size} of {availableItems.length} selected</span>
                      <button onClick={toggleSelectAll} data-testid="work-order-deliverables-select-all" className="text-[10px] text-cyan-400 hover:text-cyan-300">
                        {selectedDeliverables.size === availableItems.length && availableItems.length > 0 ? 'Deselect All' : 'Select All'}
                      </button>
                    </div>
                  </div>

                  {deliverablesLoading && !isSound ? (
                    <div className="py-8 text-center text-xs text-zinc-600 animate-pulse">Fetching deliverables...</div>
                  ) : availableItems.length === 0 ? (
                    <div className="py-8 text-center">
                      <p className="text-xs text-zinc-500">No deliverables found for {deptObj.label}</p>
                      <p className="text-[10px] text-zinc-600 mt-1">
                        {department === 'animatics'
                          ? 'No shots in this project yet. Add shots to the project first.'
                          : 'Create assets/shots in the project first, or try a different department'}
                      </p>
                    </div>
                  ) : (
                    <>
                      {department === 'animatics' && (
                        <div className="mb-3 px-3 py-2 bg-cyan-500/10 border border-cyan-500/30 rounded-sm flex items-start gap-2">
                          <FileText className="w-3.5 h-3.5 text-cyan-400 flex-shrink-0 mt-0.5" />
                          <p className="text-[10px] text-cyan-300 leading-relaxed">
                            <span className="font-semibold">Each animatic deliverable = 1 MP4 video</span> per shot.
                            Vendor uploads a rendered MP4 (with audio + timing) for each selected shot.
                          </p>
                        </div>
                      )}
                      <div className="space-y-2 max-h-[380px] overflow-y-auto scrollbar-thin scrollbar-thumb-zinc-700 pr-1">
                      {availableItems.map(item => {
                        const id = item.id || item.code;
                        const isSelected = selectedDeliverables.has(id);
                        const isAssigned = item.assigned_to && item.assigned_to !== '';
                        return (
                          <div key={id} className={`rounded-sm transition-all ${
                            isSelected ? 'bg-cyan-500/10 border border-cyan-500/40' : 'bg-zinc-900 border border-zinc-800 hover:border-zinc-600'
                          }`}>
                            <button onClick={() => toggleDeliverable(id)} data-testid={`work-order-deliverable-${id}`}
                              className="w-full flex items-center gap-3 px-3 py-2.5 text-left">
                              <div className={`w-5 h-5 rounded-sm flex items-center justify-center flex-shrink-0 ${
                                isSelected ? 'bg-cyan-500 text-zinc-950' : 'border border-zinc-700'
                              }`}>
                                {isSelected && <Check className="w-3 h-3" />}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-xs text-zinc-200 truncate">{item.name || item.code}</p>
                                <p className="text-[9px] text-zinc-600">{item.category} {isAssigned ? '\u00B7 Assigned' : ''}</p>
                              </div>
                              {item.status && item.status !== 'not_started' && (
                                <span className={`text-[8px] px-1.5 py-0.5 rounded-full flex-shrink-0 ${
                                  item.status === 'completed' ? 'bg-emerald-500/15 text-emerald-400' :
                                  item.status === 'in_progress' ? 'bg-blue-500/15 text-blue-400' :
                                  'bg-zinc-700/50 text-zinc-500'
                                }`}>{item.status.replace(/_/g, ' ')}</span>
                              )}
                            </button>
                            {isSelected && (
                              <div className="px-3 pb-3 pt-0">
                                <input
                                  value={deliverableNotes[id] || ''}
                                  onChange={e => setDeliverableNotes(prev => ({ ...prev, [id]: e.target.value }))}
                                  data-testid={`work-order-deliverable-notes-${id}`}
                                  placeholder={isSound ? 'Specs / duration / mood / reference track…' : 'Specs / notes for this deliverable…'}
                                  className="w-full h-8 bg-zinc-950 border border-zinc-800 px-2.5 text-[11px] text-zinc-200 rounded-sm focus:outline-none focus:border-cyan-500 placeholder:text-zinc-600" />
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    </>
                  )}
                </div>

                {isSound ? (
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-3">
                    <div>
                      <label className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1 block">Mix Format</label>
                      <select value={form.sound_mix} onChange={e => field('sound_mix', e.target.value)} data-testid="work-order-sound-mix-select" className="w-full h-10 bg-zinc-950 border border-zinc-800 px-3 text-xs focus:outline-none rounded-sm">
                        <option>Stereo</option><option>5.1 Surround</option><option>7.1 Surround</option><option>Dolby Atmos</option><option>Pre-Mix</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1 block">Audio Format</label>
                      <select value={form.sound_format} onChange={e => field('sound_format', e.target.value)} data-testid="work-order-sound-format-select" className="w-full h-10 bg-zinc-950 border border-zinc-800 px-3 text-xs focus:outline-none rounded-sm">
                        <option>WAV</option><option>AIFF</option><option>FLAC</option><option>MP3</option><option>AAC</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1 block">Sample Rate</label>
                      <select value={form.sound_sample_rate} onChange={e => field('sound_sample_rate', e.target.value)} data-testid="work-order-sound-sample-rate-select" className="w-full h-10 bg-zinc-950 border border-zinc-800 px-3 text-xs focus:outline-none rounded-sm">
                        <option>44.1 kHz</option><option>48 kHz</option><option>88.2 kHz</option><option>96 kHz</option><option>192 kHz</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1 block">Bit Depth</label>
                      <select value={form.sound_bit_depth} onChange={e => field('sound_bit_depth', e.target.value)} data-testid="work-order-sound-bit-depth-select" className="w-full h-10 bg-zinc-950 border border-zinc-800 px-3 text-xs focus:outline-none rounded-sm">
                        <option>16-bit</option><option>24-bit</option><option>32-bit float</option>
                      </select>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-3">
                    <div>
                      <label className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1 block">Resolution</label>
                      <input value={form.resolution} onChange={e => field('resolution', e.target.value)} data-testid="work-order-resolution-input" className="w-full h-10 bg-zinc-950 border border-zinc-800 px-3 text-xs focus:outline-none rounded-sm" />
                    </div>
                    <div>
                      <label className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1 block">Format</label>
                      <input value={form.format} onChange={e => field('format', e.target.value)} data-testid="work-order-format-input" className="w-full h-10 bg-zinc-950 border border-zinc-800 px-3 text-xs focus:outline-none rounded-sm" />
                    </div>
                    <div>
                      <label className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1 block">Frame Rate (fps)</label>
                      <input value={form.frame_rate} onChange={e => field('frame_rate', e.target.value)} data-testid="work-order-frame-rate-input" className="w-full h-10 bg-zinc-950 border border-zinc-800 px-3 text-xs focus:outline-none rounded-sm" />
                    </div>
                    <div>
                      <label className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1 block">Audio Required</label>
                      <select value={form.audio} onChange={e => field('audio', e.target.value)} data-testid="work-order-audio-select" className="w-full h-10 bg-zinc-950 border border-zinc-800 px-3 text-xs focus:outline-none rounded-sm"><option>Yes</option><option>No</option></select>
                    </div>
                  </div>
                )}
                <div className="mt-3">
                  <label className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1 block">Scope Exclusions</label>
                  <textarea value={form.exclusions} onChange={e => field('exclusions', e.target.value)} data-testid="work-order-exclusions-input" className="w-full h-20 bg-zinc-950 border border-zinc-800 px-3 py-2 text-xs focus:outline-none resize-none rounded-sm" />
                </div>
              </section>

              {/* ─── 3. DEPARTMENT RATE CARD ─── */}
              <section className="bg-zinc-900 border border-zinc-800 rounded-sm p-4" data-testid="work-order-rate-card-section">
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                  <h3 className="text-xs uppercase tracking-wider text-zinc-300 flex items-center gap-2"><Calculator className="w-4 h-4 text-emerald-400" /> 3. Department Rate Card</h3>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={autoPopulate}
                      disabled={autoBusy || !projectId || !selectedVendor}
                      data-testid="work-order-auto-populate-btn"
                      title="Pull allotted-but-unbilled shots/assets for this vendor and fill them at project rate-card prices"
                      className="h-8 px-3 bg-violet-500 hover:bg-violet-400 disabled:opacity-40 disabled:cursor-not-allowed text-white text-[11px] font-bold uppercase rounded-sm flex items-center gap-1.5"
                    >
                      <Zap className="w-3.5 h-3.5" />
                      {autoBusy ? 'Pulling…' : 'Auto-populate from allotments'}
                    </button>
                    <button onClick={() => setLines([...lines, { ...emptyLine }])} data-testid="work-order-add-line-button" className="h-8 px-3 bg-zinc-800 hover:bg-zinc-700 text-xs flex items-center gap-1 rounded-sm"><Plus className="w-3 h-3" /> Add Line</button>
                  </div>
                </div>
                {/* Auto-populate result banner — tells the PM how the lines came in. */}
                {autoSummary && (
                  <div
                    className="mb-3 px-3 py-2 bg-violet-500/10 border border-violet-500/30 rounded-sm flex items-start gap-2 text-[11px] text-violet-200"
                    data-testid="work-order-auto-populate-summary"
                  >
                    <Zap className="w-3.5 h-3.5 text-violet-400 mt-0.5 shrink-0" />
                    <div className="flex-1">
                      <p>
                        Filled from <span className="font-bold text-violet-100">{autoSummary.tasks_to_bill_count}</span> unbilled task{autoSummary.tasks_to_bill_count === 1 ? '' : 's'} for{' '}
                        <span className="font-bold text-violet-100">{autoSummary.assignee_name || 'assignee'}</span>.{' '}
                        {autoSummary.already_billed_task_count > 0 && (
                          <span className="opacity-80">
                            ({autoSummary.already_billed_task_count} task{autoSummary.already_billed_task_count === 1 ? '' : 's'} already on a prior WO — skipped.)
                          </span>
                        )}
                      </p>
                      {autoSummary.rate_card_missing && (
                        <p className="mt-0.5 text-amber-300">
                          ⚠ No locked project rate card — used default rates.{' '}
                          <a href={`/projects/${projectId}/rate-card`} target="_blank" rel="noreferrer" className="underline">Set one here</a>.
                        </p>
                      )}
                      {autoSummary.rate_card_locked && (
                        <p className="mt-0.5 text-emerald-300 flex items-center gap-1">
                          <Lock className="w-3 h-3" /> Rates pulled from the locked project rate card.
                        </p>
                      )}
                    </div>
                  </div>
                )}
                <div className="overflow-x-auto">
                  <table className="w-full text-xs min-w-[900px]">
                    <thead className="text-[9px] uppercase text-zinc-500 border-b border-zinc-800"><tr><th className="text-left py-2">Section</th><th className="text-left">Pipeline</th><th className="text-left">Task</th><th>Unit</th><th>Qty</th><th>Rate</th><th>Total</th><th></th></tr></thead>
                    <tbody>
                      {lines.map((line, i) => (
                        <tr key={i} className="border-b border-zinc-800/50" data-testid={`work-order-rate-line-${i}`}>
                          <td className="py-2"><select value={line.section} onChange={e => updateLine(i, { section: e.target.value })} data-testid={`work-order-line-section-${i}`} className="w-40 h-8 bg-zinc-950 border border-zinc-800 px-2 focus:outline-none rounded-sm">{SECTION_OPTIONS.map(o => <option key={o}>{o}</option>)}</select></td>
                          <td><select value={line.stage} onChange={e => updateLine(i, { stage: e.target.value })} data-testid={`work-order-line-stage-${i}`} className="w-36 h-8 bg-zinc-950 border border-zinc-800 px-2 focus:outline-none rounded-sm">{STAGE_OPTIONS.map(o => <option key={o} value={o}>{o.replace(/_/g, ' ')}</option>)}</select></td>
                          <td><input value={line.task} onChange={e => updateLine(i, { task: e.target.value })} data-testid={`work-order-line-task-${i}`} className="w-44 h-8 bg-zinc-900 border border-zinc-700 px-2 focus:outline-none rounded-sm text-amber-400/80" /></td>
                          <td><input value={line.unit} onChange={e => updateLine(i, { unit: e.target.value })} data-testid={`work-order-line-unit-${i}`} className="w-20 h-8 bg-zinc-900 border border-zinc-700 px-2 text-center focus:outline-none rounded-sm text-cyan-400/80" /></td>
                          <td><input type="number" value={line.quantity} onChange={e => updateLine(i, { quantity: e.target.value })} data-testid={`work-order-line-qty-${i}`} className="w-20 h-8 bg-zinc-950 border border-zinc-800 px-2 text-center focus:outline-none rounded-sm" /></td>
                          <td><input type="number" value={line.rate} onChange={e => updateLine(i, { rate: e.target.value })} data-testid={`work-order-line-rate-${i}`} className="w-24 h-8 bg-zinc-950 border border-zinc-800 px-2 text-center focus:outline-none rounded-sm" /></td>
                          <td className="text-right font-mono text-emerald-400">{currency(Number(line.quantity || 0) * Number(line.rate || 0))}</td>
                          <td className="text-right"><button onClick={() => setLines(lines.filter((_, idx) => idx !== i))} data-testid={`work-order-line-delete-${i}`} className="w-7 h-7 text-red-400 hover:bg-red-500/10 rounded-sm"><Trash2 className="w-3.5 h-3.5 mx-auto" /></button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>

              {/* ─── 5. PAYMENT + REVISION POLICY ─── */}
              <section className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-4" data-testid="work-order-payment-section">
                  <h3 className="text-xs uppercase tracking-wider text-zinc-300 mb-3 flex items-center gap-2"><IndianRupee className="w-4 h-4 text-emerald-400" /> 5. Payment + Revision Policy</h3>
                  <p className="text-[10px] text-zinc-600 mb-2">Payment Split (%)</p>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="text-[10px] text-zinc-500 mb-1 block">Advance %</label>
                      <input type="number" value={form.advance_percent} onChange={e => field('advance_percent', e.target.value)} data-testid="work-order-advance-percent-input" className="w-full h-9 bg-zinc-950 border border-zinc-800 px-2 text-xs focus:outline-none rounded-sm" />
                    </div>
                    <div>
                      <label className="text-[10px] text-zinc-500 mb-1 block">Mid Milestone %</label>
                      <input type="number" value={form.mid_percent} onChange={e => field('mid_percent', e.target.value)} data-testid="work-order-mid-percent-input" className="w-full h-9 bg-zinc-950 border border-zinc-800 px-2 text-xs focus:outline-none rounded-sm" />
                    </div>
                    <div>
                      <label className="text-[10px] text-zinc-500 mb-1 block">Final Delivery %</label>
                      <input type="number" value={form.final_percent} onChange={e => field('final_percent', e.target.value)} data-testid="work-order-final-percent-input" className="w-full h-9 bg-zinc-950 border border-zinc-800 px-2 text-xs focus:outline-none rounded-sm" />
                    </div>
                  </div>
                  <p className="text-[10px] text-zinc-600 mt-3 mb-2">Revision Policy</p>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="text-[10px] text-zinc-500 mb-1 block">Included Rounds</label>
                      <input type="number" value={form.revision_rounds} onChange={e => field('revision_rounds', e.target.value)} data-testid="work-order-revision-rounds-input" className="w-full h-9 bg-zinc-950 border border-zinc-800 px-2 text-xs focus:outline-none rounded-sm" />
                    </div>
                    <div>
                      <label className="text-[10px] text-zinc-500 mb-1 block">Minor Revision Cost</label>
                      <input type="number" value={form.minor_revision_cost} onChange={e => field('minor_revision_cost', e.target.value)} data-testid="work-order-minor-revision-cost-input" className="w-full h-9 bg-zinc-950 border border-zinc-800 px-2 text-xs focus:outline-none rounded-sm" />
                    </div>
                    <div>
                      <label className="text-[10px] text-zinc-500 mb-1 block">Major Revision Cost</label>
                      <input type="number" value={form.major_revision_cost} onChange={e => field('major_revision_cost', e.target.value)} data-testid="work-order-major-revision-cost-input" className="w-full h-9 bg-zinc-950 border border-zinc-800 px-2 text-xs focus:outline-none rounded-sm" />
                    </div>
                  </div>
                </div>
                <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-4" data-testid="work-order-legal-section">
                  <h3 className="text-xs uppercase tracking-wider text-zinc-300 mb-3 flex items-center gap-2"><Gavel className="w-4 h-4 text-rose-400" /> 6. Legal Terms</h3>
                  {[
                    { key: 'ip_ownership', label: 'IP Ownership' },
                    { key: 'usage_rights', label: 'Usage Rights' },
                    { key: 'confidentiality', label: 'Confidentiality / NDA' },
                    { key: 'termination', label: 'Termination Clause' },
                    { key: 'delay_penalty', label: 'Delay Penalty' },
                  ].map(({ key, label }) => (
                    <div key={key} className="mb-2">
                      <label className="text-[10px] text-zinc-500 mb-0.5 block">{label}</label>
                      <input value={form[key]} onChange={e => field(key, e.target.value)} data-testid={`work-order-${key.replace(/_/g, '-')}-input`} className="w-full h-8 bg-zinc-950 border border-zinc-800 px-2 text-xs focus:outline-none rounded-sm" />
                    </div>
                  ))}
                </div>
              </section>

              {/* ─── 7. NOTES ─── */}
              <section className="bg-zinc-900 border border-zinc-800 rounded-sm p-4">
                <h3 className="text-xs uppercase tracking-wider text-zinc-300 mb-3">7. Internal Notes</h3>
                <textarea value={form.notes} onChange={e => field('notes', e.target.value)} data-testid="work-order-notes-input" className="w-full h-20 bg-zinc-950 border border-zinc-800 px-3 py-2 text-xs resize-none rounded-sm" placeholder="Internal commercial notes / delivery terms / special instructions" />
              </section>
            </div>

            {/* ─── SIDEBAR: COST SUMMARY ─── */}
            <aside className="space-y-4 xl:sticky xl:top-4 self-start">
              <div className="bg-zinc-900 border border-amber-500/30 rounded-sm p-4" data-testid="work-order-cost-summary">
                <h3 className="text-xs uppercase tracking-wider text-amber-300 mb-3">4. Cost Summary</h3>
                <div className="space-y-2 text-xs">
                  <div className="flex justify-between"><span className="text-zinc-500">Subtotal</span><span className="font-mono">{currency(totals.subtotal)}</span></div>
                  <div className="flex justify-between items-center"><span className="text-zinc-500">GST %</span><input type="number" value={form.gst_percent} onChange={e => field('gst_percent', e.target.value)} data-testid="work-order-gst-percent-input" className="w-20 h-7 bg-zinc-950 border border-zinc-800 px-2 text-right rounded-sm" /></div>
                  <div className="flex justify-between"><span className="text-zinc-500">Tax</span><span className="font-mono">{currency(totals.gst)}</span></div>
                  <div className="pt-3 border-t border-zinc-800 flex justify-between"><span className="text-white">Total Project Cost</span><span className="text-lg font-mono text-emerald-400" data-testid="work-order-total-amount">{currency(totals.total)}</span></div>
                </div>
                <div className="mt-4 space-y-1 text-[10px] text-zinc-500">
                  <p>Advance: <span className="text-zinc-300">{currency(totals.advance)}</span></p>
                  <p>Mid Milestone: <span className="text-zinc-300">{currency(totals.mid)}</span></p>
                  <p>Final Delivery: <span className="text-zinc-300">{currency(totals.final)}</span></p>
                </div>
                <button onClick={create} disabled={saving || (!editingWO && !selectedVendor) || !projectId} data-testid="work-order-create-button" className="mt-4 w-full h-10 bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-zinc-950 font-bold text-xs flex items-center justify-center gap-2 rounded-sm"><Send className="w-4 h-4" /> {saving ? (editingWO ? 'Saving...' : 'Generating...') : (editingWO ? (editingWO.status === 'accepted' ? 'Save & Re-issue (vendor must re-accept)' : 'Save Changes') : 'Generate Detailed Work Order')}</button>
              </div>
              <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-4 text-[10px] text-zinc-500" data-testid="work-order-control-note">
                <p className="text-zinc-300 mb-2">System Controls</p>
                <p>• Work starts after approved Concept Art / Script / Animatics.</p>
                <p>• Platform approvals trigger next departments.</p>
                <p>• Final delivery is gated by payment clearance.</p>
              </div>
            </aside>
          </div>
        </div>
      </div>
    </div>
  );
}
